import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useField, useFormikContext } from 'formik';
import { Autocomplete } from '@material-ui/lab';
import { showToast } from '@klpkonsern/ui';
import { CircularProgress, TextField, Tooltip } from '@material-ui/core';

import type { FormField } from '../interfaces';
import type { BransjeRisiko } from '../services/rtkApi';
import { useGetBransjerRisikoQuery } from '../services/rtkApi';
import { getFormFieldStyles } from '../formFieldStyles';
import classNames from 'classnames';
import { getLocalizedLabel } from '../utils';
import { sortBransjeListByValue } from '@klp/leverandor/index';

const bransjerSortedLast = ['annet', 'andre'];

export const BransjeSelector = ({
    field,
    isReadOnly,
    isRequired,
}: {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const classes = getFormFieldStyles();
    const {
        data: bransjeData,
        isLoading,
        isFetching,
    } = useGetBransjerRisikoQuery(i18n.language);
    const [, meta] = useField<string | null>(field.id);
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
        if (!meta.value) return;
        try {
            const parsed = JSON.parse(meta.value);
            if (Array.isArray(parsed) && parsed.length > 0) {
                const { id, navn } = parsed[0];
                setFieldValue(field.id, JSON.stringify({ id, navn }));
            }
        } catch {
            // parse errors are handled in the render-time try/catch below
        }
    }, [meta.value]); // eslint-disable-line react-hooks/exhaustive-deps

    const sortedBransjeData = useMemo(() => {
        if (!bransjeData) return [];
        const sortedData = sortBransjeListByValue(bransjeData);

        // Element for Annet bransje should be last in the list
        const otherElements = sortedData.filter((b) =>
            bransjerSortedLast.includes(b.navn.toLowerCase()),
        );
        if (otherElements.length > 0) {
            const filteredData = sortedData.filter(
                (b) => !bransjerSortedLast.includes(b.navn.toLowerCase()),
            );
            filteredData.push(...otherElements);
            return filteredData;
        }

        return sortedData;
    }, [bransjeData]);

    let parsedSelectedvalue: BransjeRisiko | null = null;
    try {
        const parsedFieldValue = meta.value ? JSON.parse(meta.value) : null;
        if (Array.isArray(parsedFieldValue)) {
            if (parsedFieldValue.length > 0) {
                parsedSelectedvalue = parsedFieldValue[0];
            }
        } else {
            parsedSelectedvalue = parsedFieldValue;
        }
    } catch (error) {
        showToast({
            body: t('leverandor.table.ugyldigJSONBransje'),
            type: 'error',
        });
    }

    const handleChange = (value: BransjeRisiko | null) => {
        if (!value) {
            setFieldValue(field.id, null);
            setFieldValue('iboendeRisiko', null);
            return;
        }

        const fieldValue = {
            id: value.id,
            navn: value.navn,
        };
        setFieldValue(field.id, JSON.stringify(fieldValue));
        setFieldValue('iboendeRisiko', value.risiko);
    };

    return (
        <Autocomplete
            id="bransjer"
            multiple={false}
            selectOnFocus={true}
            clearOnBlur={true}
            handleHomeEndKeys={true}
            disabled={isReadOnly}
            options={sortedBransjeData}
            getOptionLabel={(option) => option.navn}
            getOptionSelected={(option, value) => option.id === value.id}
            value={parsedSelectedvalue}
            loading={isLoading || isFetching}
            onChange={(_event, newValue) => handleChange(newValue)}
            renderOption={(option) => (
                <Tooltip
                    title={option.beskrivelse || ''}
                    placement="right"
                    arrow={true}
                    PopperProps={{ style: { zIndex: 13001 } }}
                >
                    <span>{option.navn}</span>
                </Tooltip>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth={true}
                    className={classNames({
                        [classes.editableField]: !isReadOnly,
                        [classes.readOnlyField]: isReadOnly,
                    })}
                    variant="outlined"
                    disabled={!!isReadOnly}
                    required={isRequired}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading && (
                                    <CircularProgress
                                        size={20}
                                        color="inherit"
                                    />
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    label={getLocalizedLabel('', field?.label, field?.id)}
                    helperText={meta.touched ? meta.error : ''}
                    error={meta.touched && Boolean(meta.error)}
                />
            )}
        />
    );
};
